import type { VNode } from 'vue';
import { Comment, Fragment, Text } from 'vue';

/**
 * Determines whether a slot's rendered VNodes contain any visible content.
 *
 * A slot is considered empty when it only contains comment nodes (e.g. v-if
 * branches that did not render), whitespace-only text nodes, or fragments
 * whose children are themselves empty. This mirrors the suppression behavior
 * BootstrapVue's b-popover/b-tooltip applied: a popover with no visible
 * title or body content should not render an empty box.
 *
 * Note: call this on the result of invoking the slot function
 * (e.g. `hasRenderedSlotContent(slots.default())`), not on the slot itself.
 */
export const hasRenderedSlotContent = (nodes?: VNode[]): boolean => {
  if (!nodes) return false;

  return nodes.some((node) => {
    if (node.type === Comment) return false;

    if (node.type === Text) {
      return String(node.children).trim().length > 0;
    }

    if (node.type === Fragment && Array.isArray(node.children)) {
      return hasRenderedSlotContent(node.children as VNode[]);
    }

    return true;
  });
};
